import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bike, BusFront, CarFront, Pencil, Plus, Truck } from 'lucide-react';
import { Card } from '@shared/ui/containers/Card';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { Button } from '@shared/ui/primitives/Button';
import { Fab } from '@shared/ui/primitives/Fab';
import { HeroHeader } from '@shared/ui/headers/HeroHeader';
import { pageCopy } from '@shared/lib/pageCopy';
import { fetchProfile } from '@features/profile/api';
import { fetchMemberVehicles, type MemberVehicle } from './api';

export function Vehicles(): JSX.Element {
  const navigate = useNavigate();
  const copy = pageCopy().vehicles;

  const profileQuery = useQuery({
    queryKey: ['profile'],
    queryFn: fetchProfile,
  });
  const memberId = profileQuery.data?.member_id ?? profileQuery.data?.memberId;
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', memberId],
    queryFn: () => fetchMemberVehicles(memberId!),
    enabled: !!memberId,
  });

  const header = (
    <HeroHeader
      eyebrow={copy.eyebrow}
      title={copy.title}
      subtitle={copy.subtitle}
    />
  );

  if (profileQuery.isLoading || vehiclesQuery.isLoading) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="flex flex-col gap-3 px-5 pt-5">
          <Skeleton className="h-28" />
          <Skeleton className="h-28" />
        </div>
      </div>
    );
  }

  if (profileQuery.isError || vehiclesQuery.isError) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            onRetry={() => {
              profileQuery.refetch();
              vehiclesQuery.refetch();
            }}
          />
        </div>
      </div>
    );
  }

  if (!memberId) {
    return (
      <div className="min-h-full bg-paper">
        {header}
        <div className="px-5 pt-5">
          <ErrorState
            title="No member profile found"
            description="Vehicles are linked to your member profile. Please contact estate management if this keeps happening."
          />
        </div>
      </div>
    );
  }

  const vehicles = vehiclesQuery.data ?? [];

  return (
    <div className="min-h-full bg-paper">
      {header}

      <div className="px-5 pt-5 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {vehicles.length === 0 ? (
          <EmptyState
            icon={<CarFront className="h-7 w-7" strokeWidth={1.5} />}
            title="No vehicles registered."
            description="Add your vehicles so estate records stay up to date."
            action={
              <Button onClick={() => navigate('/app/vehicles/new')}>Add vehicle</Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-3">
            {vehicles.map((vehicle) => (
              <Card key={vehicle.vehicle_id} noGrain className="p-4">
                <div className="flex gap-3">
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-lg bg-sunken hairline">
                    {vehicle.photo_url ? (
                      <img
                        src={vehicle.photo_url}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full items-center justify-center text-ink-muted">
                        {vehicleIcon(vehicle)}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display text-headline text-ink truncate">
                          {vehicle.name}
                        </p>
                        <span className="mt-1 inline-flex rounded-pill bg-primary-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-[0.12em] text-primary-700">
                          {formatVehicleTypeLabel(vehicle.vehicle_type)}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => navigate(`/app/vehicles/${vehicle.vehicle_id}/edit`)}
                      >
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.75} />
                        Edit
                      </Button>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2 text-caption text-ink-muted">
                      <VehicleDetail label="Registration" value={vehicle.registration} />
                      <VehicleDetail label="Make" value={vehicle.make} />
                      <VehicleDetail label="Colour" value={vehicle.colour} />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Fab
        draggable
        label="Add a vehicle"
        onClick={() => navigate('/app/vehicles/new')}
      >
        <Plus className="h-5 w-5" strokeWidth={2.2} />
      </Fab>
    </div>
  );
}

function VehicleDetail({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}): JSX.Element {
  return (
    <span>
      <span className="block uppercase tracking-[0.12em]">{label}</span>
      <span className="mt-0.5 block text-body-sm text-ink-secondary">
        {value?.trim() || '-'}
      </span>
    </span>
  );
}

function formatVehicleTypeLabel(value?: string | null): string {
  const text = String(value ?? '').trim();
  if (!text) return 'Vehicle';
  return text
    .split(/[_\s-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function vehicleIcon(vehicle: MemberVehicle): JSX.Element {
  const type = String(vehicle.vehicle_type ?? '').toLowerCase();
  if (type.includes('motorcycle') || type.includes('bicycle')) {
    return <Bike className="h-8 w-8" strokeWidth={1.4} />;
  }
  if (type.includes('bus') || type.includes('minibus')) {
    return <BusFront className="h-8 w-8" strokeWidth={1.4} />;
  }
  if (type.includes('truck') || type.includes('van')) {
    return <Truck className="h-8 w-8" strokeWidth={1.4} />;
  }
  return <CarFront className="h-8 w-8" strokeWidth={1.4} />;
}
